import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { normalizeApiError } from "@/shared/api/client";
import {
  calendarCapabilities,
  type CalendarAgenda,
  type CalendarEvent,
  type CalendarStatus,
  type GoogleTask,
} from "@/shared/api/calendar";
import { onTasksChanged } from "@/shared/tasks/store";
import {
  deletedGoogleTaskCanonicalClientIds,
  reconcileDeletedGoogleTaskCanonicalClientIds,
} from "@/shared/tasks/google-task-canonical-deletion-fence";

const CALENDAR_AGENDA_QUERY_ROOT = "calendar-agenda";
const GOOGLE_TASK_DAY_MS = 24 * 60 * 60 * 1_000;

export const calendarAgendaExternalRefreshPolicy = {
  refetchOnMount: "always",
  refetchOnReconnect: "always",
  refetchOnWindowFocus: "always",
  structuralSharing: calendarAgendaStructuralSharing,
} as const;

export type CalendarAgendaGoogleTaskMutation =
  | { kind: "delete"; googleTaskId: string; taskListId: string }
  | { kind: "upsert"; task: GoogleTask };

type PendingGoogleTaskMutationState = {
  providerCacheIdentity: string;
  mutations: Map<string, CalendarAgendaGoogleTaskMutation>;
};

// A provider-confirmed direct mutation can outrun Google's eventually
// consistent list read. Keep its overlay on the exact account cache object;
// WeakMap ownership prevents one Google account or disconnected cache lane
// from borrowing another account's pending result.
const pendingGoogleTaskMutationsByAgenda = new WeakMap<
  CalendarAgenda,
  PendingGoogleTaskMutationState
>();

function googleTaskIdentity(task: Pick<GoogleTask, "googleTaskId" | "taskListId">): string {
  return `${task.taskListId}:${task.googleTaskId}`;
}

function googleTaskCanonicalOwnerId(
  task: Pick<GoogleTask, "canonicalClientId" | "linkedClientId">,
): string | null {
  const ownerId = String(task.canonicalClientId || task.linkedClientId || "").trim();
  return ownerId || null;
}

function googleTaskListIdForProjection(event: CalendarEvent): string | null {
  const prefix = "google-tasks:";
  return event.calendarId.startsWith(prefix) ? event.calendarId.slice(prefix.length) || null : null;
}

/** Removes a locally deleted canonical mirror and its synthetic event without
 * treating a partial provider snapshot as proof that the deletion settled. */
export function applyGoogleTaskCanonicalDeletionFence(
  agenda: CalendarAgenda,
  deletedCanonicalClientIds: Iterable<string> = deletedGoogleTaskCanonicalClientIds(),
): CalendarAgenda {
  const deletedIds = new Set(deletedCanonicalClientIds);
  const suppressedTasks = agenda.tasks.filter(
    (task) => {
      const ownerId = googleTaskCanonicalOwnerId(task);
      const legacyAmbiguousMirror =
        task.canonicalState == null && (task.fromTodaysBrief === true || ownerId !== null);
      return (
        task.canonicalState === "tombstoned" ||
        task.canonicalState === "unknown" ||
        legacyAmbiguousMirror ||
        (ownerId !== null && deletedIds.has(ownerId))
      );
    },
  );
  if (!suppressedTasks.length) return agenda;
  const tasks = agenda.tasks.filter((task) => !suppressedTasks.includes(task));
  const events = agenda.events.filter(
    (event) => !suppressedTasks.some((task) => isGoogleTaskProjection(event, task)),
  );
  return { ...agenda, events, tasks };
}

/**
 * A list-scoped provider timeout is a partial Tasks snapshot, not proof that
 * every row in that failed list was deleted. Keep the last-good task and its
 * calendar projection for failed list lanes while successful lanes remain
 * authoritative (including deletions).
 */
export function mergeCalendarAgendaTaskSnapshots(
  previous: CalendarAgenda | undefined,
  incoming: CalendarAgenda,
  deletedCanonicalClientIds?: Iterable<string>,
): CalendarAgenda {
  const publishedMutationState = pendingGoogleTaskMutationsByAgenda.get(incoming);
  if (publishedMutationState?.mutations.size) {
    // setQueriesData itself runs structuralSharing(previous, updaterResult).
    // This tagged value is the just-confirmed direct mutation, not a provider
    // read that may confirm it. Return it intact so that first publication
    // cannot consume or drop its own pending overlay.
    return rememberPendingGoogleTaskMutations(incoming, publishedMutationState);
  }
  const pendingOverlay = previous
    ? applyPendingGoogleTaskMutations(previous, incoming)
    : { agenda: incoming, state: undefined };
  const overlaidIncoming = pendingOverlay.agenda;
  if (deletedCanonicalClientIds === undefined) {
    reconcileDeletedGoogleTaskCanonicalClientIds(overlaidIncoming.tasks);
  }
  const deletedIds =
    deletedCanonicalClientIds === undefined
      ? deletedGoogleTaskCanonicalClientIds()
      : new Set(deletedCanonicalClientIds);
  const fencedIncoming = applyGoogleTaskCanonicalDeletionFence(overlaidIncoming, deletedIds);
  const fencedPrevious = previous
    ? applyGoogleTaskCanonicalDeletionFence(previous, deletedIds)
    : undefined;
  let merged = fencedIncoming;
  if (fencedPrevious && fencedIncoming.tasksSnapshotComplete === false) {
    const syncedTaskListIds = new Set(fencedIncoming.syncedTaskListIds || []);
    const incomingTaskIds = new Set(fencedIncoming.tasks.map(googleTaskIdentity));
    const retainedTasks = fencedPrevious.tasks.filter(
      (task) =>
        !syncedTaskListIds.has(task.taskListId) && !incomingTaskIds.has(googleTaskIdentity(task)),
    );
    const incomingEventIds = new Set(
      fencedIncoming.events.map((event) => `${event.calendarId}:${event.id}`),
    );
    const retainedEvents = fencedPrevious.events.filter((event) => {
      const taskListId = googleTaskListIdForProjection(event);
      return (
        taskListId !== null &&
        !syncedTaskListIds.has(taskListId) &&
        !incomingEventIds.has(`${event.calendarId}:${event.id}`)
      );
    });
    if (retainedTasks.length || retainedEvents.length) {
      merged = {
        ...fencedIncoming,
        events: [...fencedIncoming.events, ...retainedEvents].sort(
          (left, right) => (left.startEpochMs || 0) - (right.startEpochMs || 0),
        ),
        tasks: [...fencedIncoming.tasks, ...retainedTasks],
      };
    }
  }
  return rememberPendingGoogleTaskMutations(merged, pendingOverlay.state);
}

function calendarAgendaStructuralSharing(previous: unknown, incoming: unknown): unknown {
  if (
    !incoming ||
    typeof incoming !== "object" ||
    !Array.isArray((incoming as CalendarAgenda).events) ||
    !Array.isArray((incoming as CalendarAgenda).tasks)
  ) {
    return incoming;
  }
  const priorAgenda =
    previous &&
    typeof previous === "object" &&
    Array.isArray((previous as CalendarAgenda).events) &&
    Array.isArray((previous as CalendarAgenda).tasks)
      ? (previous as CalendarAgenda)
      : undefined;
  return mergeCalendarAgendaTaskSnapshots(priorAgenda, incoming as CalendarAgenda);
}

function sameGoogleTask(
  task: Pick<GoogleTask, "googleTaskId" | "taskListId">,
  identity: Pick<GoogleTask, "googleTaskId" | "taskListId">,
): boolean {
  return task.taskListId === identity.taskListId && task.googleTaskId === identity.googleTaskId;
}

function googleTaskProjectionId(task: Pick<GoogleTask, "googleTaskId" | "taskListId">): string {
  return `google-task:${task.taskListId}:${task.googleTaskId}`;
}

function isGoogleTaskProjection(
  event: CalendarEvent,
  task: Pick<GoogleTask, "googleTaskId" | "taskListId">,
): boolean {
  return (
    event.id === googleTaskProjectionId(task) &&
    event.calendarId === `google-tasks:${task.taskListId}`
  );
}

function googleTaskProjectionForAgenda(
  task: GoogleTask,
  agenda: CalendarAgenda,
): CalendarEvent | null {
  const dueEpochMs = task.dueEpochMs;
  const timeMinEpochMs = Date.parse(String(agenda.timeMin || ""));
  const timeMaxEpochMs = Date.parse(String(agenda.timeMax || ""));
  if (
    task.status === "completed" ||
    typeof dueEpochMs !== "number" ||
    !Number.isFinite(dueEpochMs) ||
    !Number.isFinite(timeMinEpochMs) ||
    !Number.isFinite(timeMaxEpochMs) ||
    dueEpochMs < timeMinEpochMs ||
    dueEpochMs >= timeMaxEpochMs
  ) {
    return null;
  }
  return {
    id: googleTaskProjectionId(task),
    calendarId: `google-tasks:${task.taskListId}`,
    calendarSummary: task.taskListTitle || "Google Tasks",
    title: task.title,
    status: task.status,
    allDay: true,
    startEpochMs: dueEpochMs,
    endEpochMs: dueEpochMs + GOOGLE_TASK_DAY_MS,
    timeZone: agenda.timeZone || null,
    location: null,
    hangoutLink: null,
    conferenceLink: null,
    htmlLink: null,
    description: task.notes || null,
    recurringEventId: null,
    recurrence: [],
    attendees: [],
  };
}

function updateGoogleTaskProjectionEvents(
  agenda: CalendarAgenda,
  mutation: CalendarAgendaGoogleTaskMutation,
): CalendarEvent[] {
  const identity = mutation.kind === "delete" ? mutation : mutation.task;
  const withoutProjection = agenda.events.filter(
    (event) => !isGoogleTaskProjection(event, identity),
  );
  const removedProjection = withoutProjection.length !== agenda.events.length;
  if (mutation.kind === "delete") return removedProjection ? withoutProjection : agenda.events;
  const projection = googleTaskProjectionForAgenda(mutation.task, agenda);
  if (!projection) return removedProjection ? withoutProjection : agenda.events;
  return [...withoutProjection, projection].sort(
    (left, right) => (left.startEpochMs || 0) - (right.startEpochMs || 0),
  );
}

function googleTaskMutationConfirmed(incoming: GoogleTask, committed: GoogleTask): boolean {
  if (committed.etag && incoming.etag === committed.etag) return true;
  const incomingUpdated =
    typeof incoming.updatedEpochMs === "number" ? incoming.updatedEpochMs : Number.NaN;
  const committedUpdated =
    typeof committed.updatedEpochMs === "number" ? committed.updatedEpochMs : Number.NaN;
  if (
    Number.isFinite(incomingUpdated) &&
    Number.isFinite(committedUpdated) &&
    incomingUpdated > committedUpdated
  ) {
    return true;
  }
  return (
    incoming.title === committed.title &&
    (incoming.notes || null) === (committed.notes || null) &&
    (incoming.dueEpochMs ?? null) === (committed.dueEpochMs ?? null) &&
    incoming.status === committed.status &&
    (incoming.completedEpochMs ?? null) === (committed.completedEpochMs ?? null)
  );
}

function googleTaskDeleteConfirmed(
  incoming: CalendarAgenda,
  mutation: Extract<CalendarAgendaGoogleTaskMutation, { kind: "delete" }>,
): boolean {
  const row = incoming.tasks.find((task) => sameGoogleTask(task, mutation));
  if (row) return row.canonicalState === "tombstoned";
  return (
    incoming.tasksSnapshotComplete === true ||
    (incoming.syncedTaskListIds || []).includes(mutation.taskListId)
  );
}

function applyGoogleTaskMutationToAgenda(
  agenda: CalendarAgenda,
  mutation: CalendarAgendaGoogleTaskMutation,
): CalendarAgenda {
  if (mutation.kind === "delete") {
    const tasks = agenda.tasks.filter((task) => !sameGoogleTask(task, mutation));
    const events = updateGoogleTaskProjectionEvents(agenda, mutation);
    const next =
      tasks.length === agenda.tasks.length && events === agenda.events
        ? agenda
        : { ...agenda, events, tasks };
    return applyGoogleTaskCanonicalDeletionFence(next);
  }
  let replaced = false;
  const existingTasks = agenda.tasks.map((task) => {
    if (!sameGoogleTask(task, mutation.task)) return task;
    replaced = true;
    return mutation.task;
  });
  const tasks = replaced ? existingTasks : [...existingTasks, mutation.task];
  const events = updateGoogleTaskProjectionEvents(agenda, mutation);
  return applyGoogleTaskCanonicalDeletionFence({ ...agenda, events, tasks });
}

function rememberPendingGoogleTaskMutations(
  agenda: CalendarAgenda,
  state: PendingGoogleTaskMutationState | undefined,
): CalendarAgenda {
  if (state?.mutations.size) pendingGoogleTaskMutationsByAgenda.set(agenda, state);
  return agenda;
}

function applyPendingGoogleTaskMutations(
  previous: CalendarAgenda,
  incoming: CalendarAgenda,
): { agenda: CalendarAgenda; state: PendingGoogleTaskMutationState | undefined } {
  const previousState = pendingGoogleTaskMutationsByAgenda.get(previous);
  if (!previousState?.mutations.size) return { agenda: incoming, state: undefined };
  const remaining = new Map(previousState.mutations);
  let agenda = incoming;
  for (const [identity, mutation] of previousState.mutations) {
    if (mutation.kind === "delete") {
      if (googleTaskDeleteConfirmed(incoming, mutation)) {
        remaining.delete(identity);
      } else {
        agenda = applyGoogleTaskMutationToAgenda(agenda, mutation);
      }
      continue;
    }
    const providerRow = incoming.tasks.find((task) => sameGoogleTask(task, mutation.task));
    if (providerRow && googleTaskMutationConfirmed(providerRow, mutation.task)) {
      remaining.delete(identity);
    } else {
      agenda = applyGoogleTaskMutationToAgenda(agenda, mutation);
    }
  }
  return {
    agenda,
    state: remaining.size
      ? { providerCacheIdentity: previousState.providerCacheIdentity, mutations: remaining }
      : undefined,
  };
}

/**
 * Publishes the provider-confirmed result immediately into only the active
 * Google account's agenda lanes. Revalidation still follows, but a transient
 * agenda failure cannot leave a successfully updated task painted as stale.
 */
export function applyGoogleTaskMutationToCalendarAgendaCache(
  queryClient: QueryClient,
  providerCacheIdentity: string,
  mutation: CalendarAgendaGoogleTaskMutation,
): void {
  queryClient.setQueriesData<CalendarAgenda>(
    {
      predicate: (query) =>
        query.queryKey[0] === CALENDAR_AGENDA_QUERY_ROOT &&
        query.queryKey[1] === providerCacheIdentity,
    },
    (agenda) => {
      if (!agenda) return agenda;
      const next = applyGoogleTaskMutationToAgenda(agenda, mutation);
      const previousState = pendingGoogleTaskMutationsByAgenda.get(agenda);
      const mutations = new Map(
        previousState?.providerCacheIdentity === providerCacheIdentity
          ? previousState.mutations
          : [],
      );
      const identity = mutation.kind === "delete" ? mutation : mutation.task;
      mutations.set(googleTaskIdentity(identity), mutation);
      return rememberPendingGoogleTaskMutations(next, {
        providerCacheIdentity,
        mutations,
      });
    },
  );
}

export async function refreshCalendarAgendaQueries(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: [CALENDAR_AGENDA_QUERY_ROOT] });
}

export function calendarProviderCacheIdentity(
  connection:
    | Pick<CalendarStatus, "accountId" | "connected" | "connectionVersion">
    | null
    | undefined,
): string | null {
  if (connection?.connected !== true) return null;
  const accountId = connection.accountId?.trim();
  const connectionVersion = connection.connectionVersion;
  if (
    !accountId ||
    typeof connectionVersion !== "number" ||
    !Number.isSafeInteger(connectionVersion) ||
    connectionVersion < 0
  ) {
    return null;
  }
  return `account:${accountId}:v${connectionVersion}`;
}

export function calendarGrantCacheIdentity(connection: CalendarStatus | null | undefined): string {
  const capabilities = calendarCapabilities(connection);
  return `${Number(capabilities.calendarEventsWritable)}${Number(capabilities.tasksWritable)}`;
}

export function useCalendarAgendaAccountFence(
  connection: CalendarStatus | null | undefined,
): string | null {
  const queryClient = useQueryClient();
  const identity = calendarProviderCacheIdentity(connection);
  const grantIdentity = calendarGrantCacheIdentity(connection);

  useEffect(() => {
    // Do not purge while status is still unresolved. Once the server has
    // answered, retain only the active provider account's cache lane. This
    // also cleans an old account when the surface unmounted during a switch.
    if (connection == null) return;
    queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[0] === CALENDAR_AGENDA_QUERY_ROOT &&
        (identity === null ||
          query.queryKey[1] !== identity ||
          query.queryKey[2] !== grantIdentity),
    });
  }, [connection, grantIdentity, identity, queryClient]);

  useEffect(
    () =>
      onTasksChanged(() => {
        const deletedIds = deletedGoogleTaskCanonicalClientIds();
        if (!deletedIds.size) return;
        queryClient.setQueriesData<CalendarAgenda>(
          { queryKey: [CALENDAR_AGENDA_QUERY_ROOT] },
          (agenda) => {
            if (!agenda) return agenda;
            const next = applyGoogleTaskCanonicalDeletionFence(agenda, deletedIds);
            return rememberPendingGoogleTaskMutations(
              next,
              pendingGoogleTaskMutationsByAgenda.get(agenda),
            );
          },
        );
      }),
    [queryClient],
  );

  return identity;
}

export function removeAllCalendarAgendaQueries(queryClient: {
  removeQueries: (filters: { queryKey: string[] }) => unknown;
}): void {
  queryClient.removeQueries({ queryKey: [CALENDAR_AGENDA_QUERY_ROOT] });
}

export function isCalendarGrantStateError(error: unknown): boolean {
  const code = normalizeApiError(error, "calendar_failed").code;
  return (
    code === "calendar_disconnect_required" ||
    code === "calendar_not_connected" ||
    code === "calendar_refresh_token_missing" ||
    code === "google_oauth_invalid_grant" ||
    code === "calendar_connection_changed" ||
    code === "google_product_scope_required" ||
    code === "calendar_write_permission_required" ||
    code === "google_tasks_write_permission_required"
  );
}

export async function recoverCalendarGrantState(
  queryClient: QueryClient,
  error: unknown,
): Promise<boolean> {
  if (!isCalendarGrantStateError(error)) return false;
  removeAllCalendarAgendaQueries(queryClient);
  await queryClient.invalidateQueries({ queryKey: ["calendar-status"] });
  return true;
}

export function useCalendarGrantErrorRecovery(error: unknown): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isCalendarGrantStateError(error)) return;
    void recoverCalendarGrantState(queryClient, error);
  }, [error, queryClient]);
}
